import { useEffect } from 'react';

import { MarketingHeader, MarketingFooter } from '../components/MarketingChrome';
import { ResourceSidebar } from '../components/ResourceSidebar';
import { appPath } from '../lib/config';
import '../components/landing.css';
import './getting-started.css';

const PAGE_TITLE = 'Compare laps and telemetry traces on the web | Paddiq';

export function CompareLapsPage() {
  useEffect(() => {
    const previousTitle = document.title;
    document.title = PAGE_TITLE;

    return () => {
      document.title = previousTitle;
    };
  }, []);

  return (
    <div className="landing marketing-home mh-interior">
      <a className="mh-skip" href="#main">
        Skip to content
      </a>
      <MarketingHeader />

      <main id="main">
        <section className="guide-hero">
          <div className="landing-container guide-hero__inner">
            <p className="section-kicker">Session analysis guide</p>
            <h1>Compare laps and telemetry traces on the web.</h1>
            <p className="guide-hero__lead">
              Choose laps across sessions, switch between Map and Traces views, and replay the exact
              section where the data differs.
            </p>
            <div className="guide-hero__actions">
              <a
                className="landing-btn landing-btn--primary landing-btn--lg"
                href={appPath('/sessions')}
              >
                Open your sessions
              </a>
              <a
                className="landing-btn landing-btn--ghost landing-btn--lg"
                href="/resources/upload-mychron-session"
              >
                Upload a MyChron session
              </a>
            </div>
            <p className="guide-hero__meta">Web guide · Reviewed October 15, 2026</p>
          </div>
        </section>

        <section className="guide-section">
          <div className="landing-container guide-layout">
            <aside className="guide-summary" aria-label="Guide summary">
              <ResourceSidebar currentPath="/resources/compare-laps-and-traces" />
              <p className="guide-summary__label">In this guide</p>
              <ol>
                <li>
                  <a href="#primary">Choose a primary lap</a>
                </li>
                <li>
                  <a href="#compare">Add a comparison</a>
                </li>
                <li>
                  <a href="#traces">Read lap traces</a>
                </li>
                <li>
                  <a href="#turns">Compare turn speeds</a>
                </li>
                <li>
                  <a href="#overlays">Check braking and coasting</a>
                </li>
              </ol>
              <div className="guide-summary__note">
                <strong>Best comparison</strong>
                <span>
                  Start with two representative laps from the same circuit and use the same lap
                  timing mode for both.
                </span>
              </div>
            </aside>

            <article className="guide-content">
              <section className="guide-step" id="primary">
                <div className="guide-step__number" aria-hidden="true">
                  1
                </div>
                <div>
                  <p className="guide-step__eyebrow">Reference lap</p>
                  <h2>Choose the lap you want to understand.</h2>
                  <ol className="guide-instructions">
                    <li>
                      Open <strong>Sessions</strong> and select <strong>Session &amp; laps</strong>.
                    </li>
                    <li>
                      Expand a date, then a driver, and select the session time to make that run
                      primary.
                    </li>
                    <li>
                      Use <strong>Circuit Laps</strong> for laps built from the saved circuit, or{' '}
                      <strong>Raw laps</strong> for the logger&apos;s recorded lap beacons.
                    </li>
                    <li>Choose the primary lap, then close the panel.</li>
                  </ol>
                  <div className="guide-callout">
                    A useful reference is repeatable and representative. The fastest lap is not
                    always the best starting point for a coaching conversation.
                  </div>
                  <p>
                    When it is available, turn on <strong>Fastest recorded lap</strong> to add the
                    driver&apos;s quickest complete valid historical lap for the exact saved circuit
                    configuration as a dashed blue map reference. Read the{' '}
                    <a className="guide-text-link" href="/resources/fastest-recorded-lap">
                      fastest recorded lap guide
                    </a>{' '}
                    for eligibility and source details.
                  </p>
                </div>
              </section>

              <section className="guide-step" id="compare">
                <div className="guide-step__number" aria-hidden="true">
                  2
                </div>
                <div>
                  <p className="guide-step__eyebrow">Comparison lap</p>
                  <h2>Add one lap that answers a specific question.</h2>
                  <ol className="guide-instructions">
                    <li>
                      Open <strong>Session &amp; laps</strong> again.
                    </li>
                    <li>
                      Expand the date and driver folders to find another run. Older sessions
                      continue loading while the drawer is open.
                    </li>
                    <li>
                      Check another session to add it as a comparison, then choose its lap under{' '}
                      <strong>Compare laps</strong>.
                    </li>
                    <li>
                      To compare laps within one run, select <strong>Compare another lap</strong>,
                      then choose a different lap from the primary session.
                    </li>
                    <li>
                      Select a session time to make it primary without clearing your other selected
                      sessions. The folder list labels the primary and comparison runs.
                    </li>
                  </ol>
                  <p>
                    Choose representative laps from the same circuit configuration. Uncheck a
                    session to remove it from the comparison, or remove an individual comparison lap
                    from its panel.
                  </p>
                  <p>
                    The comparison rail stays visible in Map View and Traces View. Use its{' '}
                    <strong>Primary</strong> and <strong>Compare</strong> cards to change laps or
                    remove a comparison lap without removing its session. Select a card to make that
                    session the source for the full-session GPS overview; lap picks in the overview
                    apply to the active card.
                  </p>
                </div>
              </section>

              <section className="guide-step" id="traces">
                <div className="guide-step__number" aria-hidden="true">
                  3
                </div>
                <div>
                  <p className="guide-step__eyebrow">Synchronized data</p>
                  <h2>Use Lap traces to locate the difference.</h2>
                  <ol className="guide-instructions">
                    <li>
                      Select <strong>Traces View</strong> beside Map View.
                    </li>
                    <li>
                      Select <strong>Choose traces</strong>. Channels remain listed even when the
                      current session has no samples for them.
                    </li>
                    <li>
                      Play the lap or move along a chart to follow the same moment on the map.
                    </li>
                    <li>Use each chart legend to identify its lap and channel lines.</li>
                    <li>
                      Configure, reorder, or rename charts and combine up to two channels in a
                      chart. Open the three-dot <strong>Trace actions</strong> menu and choose{' '}
                      <strong>Save trace view</strong> to name and save the layout.
                    </li>
                    <li>Drag across a chart and select replay to loop only that section.</li>
                  </ol>
                  <p>
                    Saved trace views sync across devices when you sign in to the same account and
                    workspace. They are private to you within that workspace, not shared with its
                    other members. Paddiq remembers Map View or Traces View and which saved view you
                    last selected locally on this computer.
                  </p>
                  <p>
                    Older browser-only saves stay on their original browser until you choose one
                    under <strong>Configure trace view</strong>, review the destination account and
                    workspace, confirm that the save belongs to you, and select{' '}
                    <strong>Import selected save</strong>. You can also open the browser copy
                    without uploading it.
                  </p>
                  <h3>Choose how the traces line up.</h3>
                  <p>
                    In <strong>Trace actions</strong>, choose <strong>Align by Time</strong> to
                    compare recorded elapsed time, or <strong>Align by GPS</strong> to line up
                    equivalent positions along the primary lap's driven path. GPS alignment needs
                    usable GPS for every compared lap; the primary lap remains the replay and cursor
                    reference. This alignment choice is remembered on this computer.
                  </p>
                  <p>
                    A session without usable GPS can still show recorded telemetry in Traces View
                    with time alignment. Maps, GPS alignment, and GPS-derived overlays need recorded
                    position data; selecting a circuit cannot supply missing GPS.
                  </p>
                </div>
              </section>

              <section className="guide-step" id="turns">
                <div className="guide-step__number" aria-hidden="true">
                  4
                </div>
                <div>
                  <p className="guide-step__eyebrow">Corner review</p>
                  <h2>Check entry, apex, and exit speed by turn.</h2>
                  <ol className="guide-instructions">
                    <li>
                      Select <strong>Turns</strong> above the map.
                    </li>
                    <li>
                      Read each row as <strong>Entrance / Apex / Exit</strong> in your selected
                      speed unit.
                    </li>
                    <li>
                      When two laps are selected, use <strong>Compare | ΔA</strong> to see the
                      comparison lap and its apex-speed difference.
                    </li>
                    <li>Return to the traces and map to understand what led to the difference.</li>
                  </ol>
                  <p>
                    A positive apex difference means the comparison lap carried more speed at the
                    apex. Judge it with exit speed and the next section of track before deciding
                    whether it helped the lap.
                  </p>
                </div>
              </section>

              <section className="guide-step" id="overlays">
                <div className="guide-step__number" aria-hidden="true">
                  5
                </div>
                <div>
                  <p className="guide-step__eyebrow">Map interpretation</p>
                  <h2>Use braking and coasting overlays as estimates.</h2>
                  <ol className="guide-instructions">
                    <li>
                      Select <strong>Overlays</strong> and turn on <strong>Braking zones</strong>.
                    </li>
                    <li>Use the map key to distinguish soft and hard deceleration.</li>
                    <li>
                      Turn on <strong>Estimated coasting</strong> to find sustained mild
                      deceleration.
                    </li>
                    <li>Compare where each lap slows, reaches the turn, and returns to speed.</li>
                  </ol>
                  <div className="guide-callout">
                    These overlays are estimated from changes in GPS speed. They do not prove when
                    the brake pedal was pressed or the throttle was closed.
                  </div>
                </div>
              </section>

              <section className="guide-troubleshooting" id="troubleshooting">
                <p className="guide-step__eyebrow">Troubleshooting</p>
                <h2>If the comparison is not useful</h2>
                <div className="guide-troubleshooting__grid">
                  <article>
                    <h3>Circuit Laps are missing</h3>
                    <p>
                      Finish the saved circuit and start/finish setup, then return to{' '}
                      <strong>Session &amp; laps</strong>. Use the{' '}
                      <a className="guide-text-link" href="/resources/set-up-circuit-laps">
                        circuit lap setup guide
                      </a>{' '}
                      if the trace does not create usable laps.
                    </p>
                  </article>
                  <article>
                    <h3>Overlays do not open</h3>
                    <p>
                      Wait for the selected session and lap to finish loading. If the session has no
                      usable GPS trace, choose another session with recorded GPS data.
                    </p>
                  </article>
                  <article>
                    <h3>Turn speeds show dashes</h3>
                    <p>
                      Confirm that the saved circuit has turn markers and that the selected lap
                      passes through those turns.
                    </p>
                  </article>
                  <article>
                    <h3>The laps do not line up</h3>
                    <p>
                      Compare laps from the same circuit and keep both in the same{' '}
                      <strong>Circuit Laps</strong> or <strong>Raw laps</strong> timing mode.
                    </p>
                  </article>
                </div>
              </section>

              <section className="guide-cta">
                <div>
                  <p className="guide-step__eyebrow">Put it into practice</p>
                  <h2>Start with two laps and one question.</h2>
                  <p>
                    Open a session in your workspace, choose a reference lap, and compare the
                    section you want to improve.
                  </p>
                </div>
                <div className="guide-cta__actions">
                  <a
                    className="landing-btn landing-btn--primary landing-btn--lg"
                    href={appPath('/sessions')}
                  >
                    Open your sessions
                  </a>
                  <a className="landing-btn landing-btn--ghost landing-btn--lg" href="/support">
                    Contact support
                  </a>
                </div>
              </section>
            </article>
          </div>
        </section>
      </main>

      <MarketingFooter />
    </div>
  );
}
